import { DestroyRef, inject, Injectable, InputSignal, OnDestroy, signal, Type } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ExamDojoFeatureFlag } from '@examdojo/models/feature-flag';
import { PlatformService } from '@examdojo/platform';
import { ModalController, Platform } from '@ionic/angular/standalone';
import type { ModalOptions } from '@ionic/core/components';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  pairwise,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { DialogOptions } from './dialog-options.model';

@Injectable()
export abstract class AbstractDialogService<Input = void, Output = boolean, C extends Type<unknown> = Type<unknown>>
  implements OnDestroy
{
  readonly modalCtrl = inject(ModalController);
  readonly platform = inject(Platform);
  readonly platformService = inject(PlatformService);
  readonly destroyRef = inject(DestroyRef);
  readonly featureFlagService = inject(FeatureFlagService);
  readonly errorHandlerService = inject(ErrorHandlerService);

  abstract readonly id: string;
  protected abstract readonly component: C;
  protected readonly componentProps: Partial<InstanceType<C>> = {};

  protected readonly options?: DialogOptions;
  protected readonly dismissOnBreakpointChange: boolean = false;

  protected readonly preparing = signal(false);
  readonly dialogRef = signal<HTMLIonModalElement | undefined>(undefined);
  dialogInput?: Input;

  readonly isMobile = this.platform.is('mobile');
  readonly isIpad = this.platform.is('ipad');

  readonly dismissed$ = new Subject<void>();
  readonly opened$ = new Subject<void>();

  readonly isOpen$ = combineLatest([toObservable(this.dialogRef), toObservable(this.preparing)]).pipe(
    map(([dialogRef, preparing]) => preparing || (!!dialogRef && dialogRef.id === this.id)),
  );

  protected getPlatformSpecificOptions(isSingleBreakpoint = false): Partial<ModalOptions> {
    if (this.isMobile && !this.isIpad) {
      return isSingleBreakpoint
        ? { initialBreakpoint: 1, breakpoints: [1] }
        : { initialBreakpoint: 1, breakpoints: [0, 1] };
    }
    // no breakpoints for desktop
    return {};
  }

  private readonly defaultModalConfig = {
    cssClass: 'auto-height action-sheet-on-mobile',
    canDismiss: true,
    backdropDismiss: true,
    showBackdrop: true,
    handle: true,
  } satisfies Partial<ModalOptions>;

  assignComponentProperty<T extends keyof InstanceType<C>, U extends InstanceType<C>[T]>(
    property: T,
    value: U extends InputSignal<infer V> ? V : U,
  ) {
    // @ts-ignore ModalController supports setting both decorated and signal inputs - https://github.com/ionic-team/ionic-framework/pull/29453
    this.componentProps[property] = value;
  }

  async openDialog({
    input,
    options,
    isSingleBreakpoint = false,
    beforeDismissCallback,
  }: {
    input?: Input;
    options?: DialogOptions;
    isSingleBreakpoint?: boolean;
    beforeDismissCallback?: () => unknown | Promise<unknown>;
  } = {}): Promise<Output | undefined> {
    this.dialogInput = input;
    const platformSpecificOptions = this.getPlatformSpecificOptions(isSingleBreakpoint);
    const commitDialogWrapperAnimationStyles = this.featureFlagService.get(ExamDojoFeatureFlag.DialogWrapperFix);

    if (this.isOpen()) {
      console.warn(`Dialog ${this.id} is already opened`);
      return;
    }

    this.preparing.set(true);

    // Ensure any other dialog with this id is dismissed
    // In some cases, a reference will be kept in the ModalController, despite the modal being dismissed
    await this.dismiss();

    const dialogRef = await this.modalCtrl.create({
      ...this.defaultModalConfig,
      ...platformSpecificOptions,
      id: this.id,
      component: this.component,
      componentProps: this.componentProps,
      ...(this.options ?? {}),
      ...(options ?? {}),
    });

    this.dialogRef.set(dialogRef);
    this.preparing.set(false);

    if (commitDialogWrapperAnimationStyles) {
      fromEvent(dialogRef, 'ionModalDidPresent')
        .pipe(
          tap(() => {
            this.commitDialogWrapperAnimation(dialogRef);
          }),
          takeUntilDestroyed(this.destroyRef),
          takeUntil(this.dismissed$),
        )
        .subscribe();
    }

    this.opened$.next();
    await dialogRef.present();

    if (this.dismissOnBreakpointChange) {
      this.dismissWhenPassingBreakpointsOnResize()
        .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.dismissed$))
        .subscribe();
    }

    await dialogRef.onWillDismiss();
    if (beforeDismissCallback) {
      await beforeDismissCallback();
    }

    // `onDidDismiss` is not being called when the dialog swiped down on mobile
    // Therefore these need to be set here
    this.dialogRef.set(undefined);
    this.dismissed$.next();

    const result = await dialogRef.onDidDismiss<Output>();
    if (commitDialogWrapperAnimationStyles) {
      this.commitDialogWrapperAnimation(dialogRef);
    }

    return result.data;
  }

  isOpen() {
    const ref = this.dialogRef();
    const preparing = this.preparing();
    return preparing || (!!ref && ref.id === this.id);
  }

  async dismiss(data?: Output) {
    let result: boolean | undefined;
    try {
      result = await this.modalCtrl.dismiss(data, '', this.id);
    } catch (_) {
      // do nothing
    }
    this.dialogRef.set(undefined);
    return result;
  }

  ngOnDestroy() {
    this.dismiss();
  }

  private dismissWhenPassingBreakpointsOnResize() {
    return this.platformService.resize$.pipe(
      map(() => this.platformService.width >= this.platformService.smallBreakpoint),
      distinctUntilChanged(),
      pairwise(),
      filter(([prev, curr]) => prev !== curr),
      switchMap(() => this.dismiss()),
    );
  }

  private commitDialogWrapperAnimation(dialogRef: HTMLIonModalElement) {
    dialogRef?.shadowRoot?.querySelectorAll('.ion-overlay-wrapper').forEach((wrapper) => {
      try {
        wrapper.getAnimations()?.[0]?.commitStyles();
      } catch (error) {
        this.errorHandlerService.error('Failed to commitStyles', {
          err: error,
          context: {
            id: this.id,
          },
        });
      }
    });
  }
}
